import { checkoutWorkKey } from "./postOpSuggestions";

export function isPendingClinicalSaveOrthoRow(row) {
  return row?.source === "ortho_checkout" && row?.payable_status === "pending_clinical_save";
}

export function requiresSavedOrthoClinicalRecord(row) {
  return isPendingClinicalSaveOrthoRow(row) && row?.ortho_visit_type === "debonding";
}

function sameCheckoutWork(a, b) {
  const ak = checkoutWorkKey(a);
  const bk = checkoutWorkKey(b);
  if (ak && bk) return ak === bk;
  return !!(a?.tn_id && b?.tn_id && a.tn_id === b.tn_id);
}

export function isPendingClinicalSaveResolved(row, items = [], chairsideDispositions = {}) {
  if (!isPendingClinicalSaveOrthoRow(row)) return true;

  const key = checkoutWorkKey(row);
  const disposition = (key ? chairsideDispositions?.[key] : null)
    || (row?.tn_id ? chairsideDispositions?.[`tn:${row.tn_id}`] : null);

  // Debonding is the terminal clinical event. A front-desk "Done" click
  // cannot substitute for the doctor's saved Debonding record because that
  // DB write creates the terminal charge and determines clean close vs AR.
  if (requiresSavedOrthoClinicalRecord(row)) {
    return disposition?.kind === "not_today" || disposition?.kind === "declined";
  }

  if ((items || []).some((item) => sameCheckoutWork(item, row) && (item.outcome || "done") === "done")) {
    return true;
  }

  return disposition?.kind === "not_today" || disposition?.kind === "declined";
}

export function hasUnresolvedPendingClinicalSave({
  rows = [],
  items = [],
  chairsideDispositions = {},
} = {}) {
  return (rows || []).some((row) => (
    isPendingClinicalSaveOrthoRow(row)
    && !isPendingClinicalSaveResolved(row, items, chairsideDispositions)
  ));
}

export function seedFrontDeskConfirmedOrthoItem(row) {
  if (!isPendingClinicalSaveOrthoRow(row)) return row;
  return {
    ...row,
    work_key: checkoutWorkKey(row),
    source: null,
    payable_status: null,
    price_source: "front_desk_confirmed_pending_ortho",
  };
}

export function seedCheckoutScheduledItem(scheduledRow) {
  const row = seedFrontDeskConfirmedOrthoItem(scheduledRow);
  const key = checkoutWorkKey(row);
  return {
    // Preserve every DB-authored Ortho field, especially terminal-balance
    // centavos and final-visit flags. Checkout may normalize ordinary item
    // fields below, but it must never reconstruct the Ortho balance itself.
    ...row,
    tn_id:                    row.tn_id,
    procedure_visit_id:       row.procedure_visit_id || null,
    procedure_instance_id:    row.procedure_instance_id || null,
    charge_event_id:          row.charge_event_id || null,
    work_key:                 key,
    source:                   row.source || null,
    payable_status:           row.payable_status || null,
    price_source:             row.price_source || null,
    ortho_case_id:            row.ortho_case_id || null,
    ortho_visit_type:         row.ortho_visit_type || null,
    procedure_id:             row.procedure_id,
    procedure_code:           row.procedure_code,
    procedure_name:           row.procedure_name,
    default_fee_centavos:     row.default_fee_centavos,
    performed_price_centavos: row.performed_price_centavos ?? row.display_price_centavos ?? row.default_fee_centavos,
    item_discount_centavos:   0,
    qty:                      1,
    tooth_number:             row.tooth_number || "",
    surface:                  row.surface || null,
    outcome:                  "done",
    treatment_plan_id:        row.treatment_plan_id,
    treatment_plan_item_id:   row.treatment_plan_item_id,
  };
}
